import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { roleTypes } from "../../../../shared/types/types";
import { List, Typography, Box } from "@mui/material";
import { setMenuState } from "../../../../actions/dbUiActions";
import { SidebarLink } from "../SidebarLink";
import { useAppSelector } from "../../../../hooks";
import { RootState } from "../../../../store/store";
import { activeSidebarLink, visibleSidebarSections } from "./menuConfig";
import { fetchPendingAcknowledgments } from "../../documentation/docs/documentsApi";

const PENDING_OVERDUE_DAYS = 7;

export const Sidebar: React.FC = () => {
  const dispatch = useDispatch();
  const { menuState, isSmallScreen } = useSelector(
    (state: RootState) => state.dbUi
  );
  const { role } = useAppSelector((state) => state.authReducer.user!);
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    setIsAuthorized(role === roleTypes.admin);
  }, [role]);

  const { data: pendingAcks } = useQuery({
    queryKey: ['pending-acknowledgments'],
    queryFn: fetchPendingAcknowledgments,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60_000,
  });
  const pendingCount = pendingAcks?.length ?? 0;
  const hasOverdue = (pendingAcks ?? []).some(
    (p) => p.pendingDays >= PENDING_OVERDUE_DAYS
  );

  const handleNavLinkClick = () => {
    if (isSmallScreen) {
      dispatch(setMenuState("close"));
    }
  };


  // El rol se pasa: el filtro vive con la configuración, que es donde puede además descartar
  // los grupos que se quedan sin nada. Filtrar aquí dejaba encabezados vacíos en pantalla.
  const sections = visibleSidebarSections(role);
  // Una sola entrada encendida: la coincidencia más larga. Si lo decide cada una por su cuenta,
  // se encienden todas las que sean prefijo de la ruta actual.
  const activo = activeSidebarLink(location.pathname, role);

  return (
    <List sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {sections.map((section, sectionIndex) => (
        <React.Fragment key={section.title}>
          {/*
            Cada grupo lleva su color (`section.accent`), y se nota en tres sitios: el
            encabezado, una barrita a su izquierda y los iconos de sus entradas. Con nueve
            grupos todos en el mismo gris, la barra se leía como una lista larga en vez de como
            nueve bloques.

            La línea de arriba separa un grupo del anterior — en el primero no, que sería una
            raya colgando del borde.
          */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              pt: sectionIndex === 0 ? (isSmallScreen ? 0.5 : 1) : isSmallScreen ? 1.5 : 2,
              pb: isSmallScreen ? 0.5 : 0.75,
              mt: sectionIndex === 0 ? 0 : 0.5,
              borderTop: sectionIndex === 0 ? "none" : "1px solid",
              borderTopColor: "divider",
            }}
          >
            <Box
              sx={{
                width: 3,
                height: 14,
                borderRadius: 2,
                backgroundColor: section.accent,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: section.accent,
                fontWeight: 700,
                fontSize: isSmallScreen ? "1.1rem" : "1.1rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {section.title}
            </Typography>
          </Box>
          {section.items.map(({ link, icon, label }) => {
            const isDocs = link === '/dashboard/documentation';
            return (
              <SidebarLink
                key={link}
                link={link}
                handleNavLinkClick={handleNavLinkClick}
                muiIcon={icon}
                label={label}
                accentColor={section.accent}
                badgeCount={isDocs ? pendingCount : undefined}
                badgeColor={isDocs && hasOverdue ? 'error' : 'warning'}
              />
            );
          })}
        </React.Fragment>
      ))}
    </List>
  );
};
